import React, { useState, useRef, useEffect } from 'react'
import { domToPng } from 'modern-screenshot'
import { generateSlidesHTML } from './SlidesExporter'
import { api } from '../api'

// Full slides export (all tabs) - used on Dashboard
export function AllSlidesButton() {
  const [exporting, setExporting] = useState(false)

  const exportAll = async () => {
    setExporting(true)
    try {
      const [workItems, featureParity, execTracker, members, summariesRaw] = await Promise.all([
        api.getWorkItems().catch(() => []),
        api.getFeatureParity().catch(() => []),
        api.getExecutiveTracker().catch(() => []),
        api.getSquadMembers().catch(() => []),
        api.getWeeklySummaries().catch(() => []),
      ])
      const weeklySummaries = {}
      ;(summariesRaw || []).forEach(s => { weeklySummaries[s.week] = s })
      const html = generateSlidesHTML({ workItems, featureParity, execTracker, members, weeklySummaries })
      downloadBlob(new Blob([html], { type: 'text/html' }), `Discover-Canvas-Squad-C2-Overview-${dateSuffix()}.html`)
    } catch (e) { console.error('Slides export failed:', e) }
    setExporting(false)
  }

  return (
    <button className="all-slides-btn" onClick={exportAll} disabled={exporting} title="Export all tabs as presentation slides">
      {exporting ? '⏳ Generating...' : '🎬 Export All Slides'}
    </button>
  )
}

// Per-tab export dropdown
export default function ExportButton({ data, filename, columns, tabType }) {
  const [open, setOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const fullName = `${filename}-${dateSuffix()}`

  const exportJSON = () => {
    downloadBlob(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }), `${fullName}.json`)
    setOpen(false)
  }

  const exportCSV = () => {
    if (!data || !data.length) return
    const cols = columns || Object.keys(data[0]).filter(k => !k.startsWith('_'))
    const escape = (v) => {
      const s = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v)
      return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s
    }
    const lines = [cols.join(',')]
    data.forEach(row => lines.push(cols.map(c => escape(row[c])).join(',')))
    downloadBlob(new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8' }), `${fullName}.csv`)
    setOpen(false)
  }

  const getPageEl = () => document.querySelector('.feature-parity-page, .work-items-page, .week-over-week-page, .executive-tracker-page, .members-page, .dashboard')

  const exportPNG = async () => {
    setOpen(false)
    await new Promise(r => setTimeout(r, 300))
    const page = getPageEl()
    if (!page) return
    try {
      const dataUrl = await domToPng(page, { backgroundColor: '#ffffff', scale: 2 })
      const res = await fetch(dataUrl)
      const blob = await res.blob()
      downloadBlob(blob, `${fullName}.png`)
    } catch (e) { console.error('PNG export failed:', e) }
  }

  const exportTabSlides = async () => {
    setOpen(false)
    setExporting(true)
    try {
      // Build data for just this tab
      const allData = { workItems: [], featureParity: [], execTracker: [], members: [], weeklySummaries: {} }

      if (tabType === 'dashboard' || tabType === 'work-items' || tabType === 'week-over-week') {
        allData.workItems = Array.isArray(data) ? data : await api.getWorkItems().catch(() => [])
        if (tabType === 'week-over-week') {
          const sums = await api.getWeeklySummaries().catch(() => [])
          ;(sums || []).forEach(s => { allData.weeklySummaries[s.week] = s })
        }
      }
      if (tabType === 'feature-parity') allData.featureParity = Array.isArray(data) ? data : []
      if (tabType === 'executive-tracker') allData.execTracker = Array.isArray(data) ? data : []
      if (tabType === 'members') allData.members = Array.isArray(data) ? data : []

      const html = generateSlidesHTML(allData, tabType)
      downloadBlob(new Blob([html], { type: 'text/html' }), `${fullName}-slides.html`)
    } catch (e) { console.error('Tab slides export failed:', e) }
    setExporting(false)
  }

  return (
    <div className="export-dropdown" ref={ref}>
      <button className="export-btn" onClick={() => setOpen(!open)} title="Export" disabled={exporting}>
        {exporting ? '⏳...' : '📥 Export'}
      </button>
      {open && (
        <div className="export-menu">
          <div className="export-menu-item" onClick={exportTabSlides}>🎬 Slides (HTML)</div>
          <div className="export-menu-item" onClick={exportPNG}>🖼️ PNG Image</div>
          <div className="export-menu-item" onClick={exportJSON}>📋 JSON</div>
          <div className="export-menu-item" onClick={exportCSV}>📊 CSV</div>
        </div>
      )}
    </div>
  )
}

function dateSuffix() { return new Date().toISOString().slice(0, 10) }

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}
